<template>
    <div class="mop-megamenu-wrapper">
        <Megamenu
            :model="processedModel"
            :orientation="orientation"
            class="w-full"
            :pt="{
                root: { class: rootClass },
                menu: { class: menuClass },
                submenu: { class: submenuClass },
                submenuHeader: { class: submenuHeaderClass },
                menuitem: { class: menuitemClass },
                menuitemContent: { class: menuitemContentClass },
                menuitemLink: { class: menuitemLinkClass },
                menuitemIcon: { class: menuitemIconClass },
                menuitemText: { class: menuitemTextClass },
                panel: { class: panelClass },
                grid: { class: gridClass },
                column: { class: columnClass }
            }"
        >
            <template #start v-if="$slots.start">
                <slot name="start"></slot>
            </template>
            <template #end v-if="$slots.end">
                <slot name="end"></slot>
            </template>
            <template #item="slotProps" v-if="$slots.item">
                <slot name="item" v-bind="slotProps"></slot>
            </template>
        </Megamenu>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import Megamenu from 'primevue/megamenu';
import type { MenuItem } from 'primevue/menuitem';

// Define the structure for PrimeVue's MegaMenu model
interface MegaMenuSubmenuItem extends MenuItem {
    items?: MenuItem[];
}

interface MegaMenuColumn {
    label?: string;
    items: MegaMenuSubmenuItem[];
}

interface MegaMenuItem extends MenuItem {
    items?: MegaMenuColumn[][];
}

interface Props {
    model?: MegaMenuItem[];
    orientation?: 'horizontal' | 'vertical';
}

const props = withDefaults(defineProps<Props>(), {
    orientation: 'horizontal',
    model: () => []
});

// Process model to ensure proper structure and add any necessary attributes
const processedModel = computed(() => {
    const processed = props.model.map(item => ({
        ...item,
        class: 'p-menuitem',
        items: item.items?.map(submenuGroup =>
            submenuGroup.map(col => ({
                ...col,
                class: 'p-megamenu-submenu',
                items: col.items?.map(subItem => ({
                    ...subItem,
                    class: 'p-menuitem'
                }))
            }))
        )
    }));

    // Cast to any to bypass type checking since we know the structure is correct
    return processed as any;
});

// Computed classes
const rootClass = computed(() => [
    'p-megamenu',
    {
        'p-megamenu-vertical': props.orientation === 'vertical',
        'p-megamenu-horizontal': props.orientation === 'horizontal'
    }
]);

const menuClass = computed(() => [
    'p-megamenu-root-list'
]);

const submenuClass = computed(() => [
    'p-megamenu-submenu',
    'p-megamenu-grid'
]);

const submenuHeaderClass = computed(() => [
    'p-megamenu-submenu-header',
    'font-semibold'
]);

const menuitemClass = computed(() => [
    'p-menuitem'
]);

const menuitemContentClass = computed(() => [
    'p-menuitem-content'
]);

const menuitemLinkClass = computed(() => [
    'p-menuitem-link',
    'flex items-center'
]);

const menuitemIconClass = computed(() => [
    'p-menuitem-icon',
    'mr-2'
]);

const menuitemTextClass = computed(() => [
    'p-menuitem-text'
]);

const panelClass = computed(() => [
    'p-megamenu-panel',
    'shadow-md'
]);

const gridClass = computed(() => [
    'p-megamenu-grid',
    'grid gap-4'
]);

const columnClass = computed(() => [
    'p-megamenu-col'
]);
</script>

<style lang="scss">
.mop-megamenu-wrapper {
    .p-megamenu {
        border: none;
        background: transparent;
        width: 100%;

        &.p-megamenu-vertical {
            width: auto;
            max-width: 300px;

            .p-megamenu-root-list {
                flex-direction: column;
            }

            .p-megamenu-panel {
                left: 100%;
                top: 0;
            }
        }

        .p-megamenu-root-list {
            padding: 0;
            margin: 0;
            list-style: none;
            display: flex;
            position: relative;
        }

        .p-menuitem {
            position: relative;
            margin: 0;

            &:hover {
                > .p-menuitem-content {
                    background: var(--surface-hover);
                }
            }
        }

        .p-menuitem-content {
            padding: 0.5rem;
            border-radius: var(--border-radius);
            transition: background-color 0.2s;
        }

        .p-menuitem-link {
            padding: 0.5rem;
            display: flex;
            align-items: center;
            text-decoration: none;
            color: var(--text-color);
            cursor: pointer;
            user-select: none;
            transition: box-shadow 0.2s;

            &:focus {
                outline: none;
                box-shadow: inset 0 0 0 0.15rem var(--primary-color);
            }

            &:hover {
                background: var(--surface-hover);
            }

            &.p-disabled {
                opacity: 0.6;
                cursor: default;
                pointer-events: none;
            }
        }

        .p-menuitem-icon {
            margin-right: 0.5rem;
            line-height: 1;
        }

        .p-menuitem-text {
            line-height: 1;
        }

        .p-megamenu-panel {
            position: absolute;
            padding: 1rem;
            background: var(--surface-overlay);
            border: 1px solid var(--surface-border);
            border-radius: var(--border-radius);
            box-shadow: 0 2px 4px -1px rgb(0 0 0 / 0.1);
            z-index: 1;
            min-width: 200px;
        }

        .p-megamenu-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
            gap: 1rem;
        }

        .p-megamenu-submenu {
            padding: 0.5rem;

            &-header {
                padding: 0.5rem;
                margin: 0;
                font-weight: 600;
                color: var(--text-color);
                background: var(--surface-section);
                border-radius: var(--border-radius);
            }
        }

        .p-megamenu-col {
            padding: 0;
        }
    }

    // RTL Support
    &[dir="rtl"] {
        .p-megamenu {
            .p-menuitem-icon {
                margin-right: 0;
                margin-left: 0.5rem;
            }

            &.p-megamenu-vertical {
                .p-megamenu-panel {
                    left: auto;
                    right: 100%;
                }
            }
        }
    }

    // Accessibility
    .p-menuitem-link:focus-visible {
        outline: 2px solid var(--primary-color);
        outline-offset: 2px;
    }

    // Transitions
    .p-megamenu-panel {
        transition: transform 0.2s, opacity 0.2s;
        transform-origin: top;

        &.p-megamenu-enter-from,
        &.p-megamenu-leave-to {
            opacity: 0;
            transform: scaleY(0.95);
        }

        &.p-megamenu-enter-active,
        &.p-megamenu-leave-active {
            transition: transform 0.2s, opacity 0.2s;
        }
    }
}
</style>
